import { BaseFactory } from 'src/services/entity-factory/FactoryInterface';
import JuridicalPerson from 'src/entities/JuridicalPerson';
import Spouse from 'src/value-objects/Spouse';
import * as moment from 'moment';
import InvestorType from 'src/entities/InvestorType';
import EnumTypes from 'src/services/EnumStates/EnumTypes';

export interface ApiJuridicalPersonResponse {
    id: number;
    name: string;
    displayName: string;
    surname: string;
    nationality: string | null;
    idNumber: string | null;
    idExpirationDate: string | null;
    idFront: string | null;
    idBack: string | null;
    birthDate: string | null;
    phoneNumber: string | null;
    notificationEmail: string | null;
    email: string | null;
    address: string | null;
    houseNumber: string | null;
    zipCode: string | null;
    city: string | null;
    province: string | null;
    country: string | null;
    maritalStatus: string | null;
    maritalEconomicStatus: string | null;
    maritalPropertyRegime: string | null;
    occupationType: string | null;
    occupation: string | null;
    company: string | null;
    spouse: Spouse | null;
    profilePicture: string | null;
    facebookProfile: string | null;
    twitterProfile: string | null;
    linkedinProfile: string | null;
    enterpriseName: string;
    officeAddress: string | null;
    establishmentDate: string | null;
    notaryName: string | null;
    notaryAddress: string | null;
    protocolNumber: string | null;
    cifNumber: string | null;
    provinceCommercialRegister: string | null;
    corporateEmail: string | null;
    volumeNumber: string | null;
    pageNumber: string | null;
    sheetNumber: string | null;
    companyProxy: string | null;
    companyProxyIncluded: boolean | null;
    establishmentRepresentativeDate: string | null;
    provinceDeed: string | null;
    notaryProxyName: string | null;
    protocolProxyNumber: string | null;
    cifDocument: string | null;
    cifDocumentImage: string | null;
    deedCompany: string | null;
    deedCompanyImage: string | null;
    companyStatutes: string | null;
    companyStatutesImage: string | null;
    realOwnership: string | null;
    realOwnershipImage: string | null;
    idRepresentatives: string | null;
    idRepresentativesImage: string | null;
    notarialDeed: string | null;
    notarialDeedImage: string | null;
    userId: number;
    personType: string;
    investorType: InvestorType;
    isPublicProfile: boolean | null;
    percentCompleted?: number;
}

interface ApiJuridicalPersonCreateRequest {
}

export class JuridicalPersonFactory extends BaseFactory<JuridicalPerson> {
  public toEntity(args: ApiJuridicalPersonResponse): JuridicalPerson {
    return new JuridicalPerson({
      id: args.id,
      name: args.name,
      displayName: args.displayName,
      surname: args.surname,
      nationality: args.nationality || undefined,
      idNumber: args.idNumber || undefined,
      idExpirationDate: args.idExpirationDate ? moment.utc(args.idExpirationDate) : undefined,
      idFront: args.idFront || undefined,
      idBack: args.idBack || undefined,
      birthDate: args.birthDate ? moment.utc(args.birthDate) : undefined,
      phoneNumber: args.phoneNumber || undefined,
      notificationEmail: args.notificationEmail || undefined,
      email: args.email || undefined,
      address: args.address || undefined,
      houseNumber: args.houseNumber || undefined,
      zipCode: args.zipCode || undefined,
      city: args.city || undefined,
      province: args.province || undefined,
      country: args.country || undefined,
      maritalStatus: EnumTypes.getMaritalStatusName(args.maritalStatus!) || undefined,
      maritalEconomicStatus: EnumTypes.getMaritalEconomicStatusName(args.maritalEconomicStatus!) || undefined,
      maritalPropertyRegime: EnumTypes.getMaritalPropertyRegimeName(args.maritalPropertyRegime!) || undefined,
      occupationType: EnumTypes.getOccupationTypesName(args.occupationType!) || undefined,
      occupation: args.occupation || undefined,
      company: args.company || undefined,
      spouse: args.spouse ? new Spouse(args.spouse) : undefined,
      profilePicture: args.profilePicture || undefined,
      facebookProfile: args.facebookProfile || undefined,
      twitterProfile: args.twitterProfile || undefined,
      linkedinProfile: args.linkedinProfile || undefined,
      enterpriseName: args.enterpriseName,
      officeAddress: args.officeAddress || undefined,
      establishmentDate: args.establishmentDate ? moment.utc(args.establishmentDate) : undefined,
      notaryName: args.notaryName || undefined,
      notaryAddress: args.notaryAddress || undefined,
      protocolNumber: args.protocolNumber || undefined,
      cifNumber: args.cifNumber || undefined,
      provinceCommercialRegister: args.provinceCommercialRegister || undefined,
      corporateEmail: args.corporateEmail || undefined,
      volumeNumber: args.volumeNumber || undefined,
      pageNumber: args.pageNumber || undefined,
      sheetNumber: args.sheetNumber || undefined,
      companyProxy: args.companyProxy || undefined,
      companyProxyIncluded: args.companyProxyIncluded || undefined,
      establishmentRepresentativeDate: args.establishmentRepresentativeDate ?
        moment.utc(args.establishmentRepresentativeDate) : undefined,
      provinceDeed: args.provinceDeed || undefined,
      notaryProxyName: args.notaryProxyName || undefined,
      protocolProxyNumber: args.protocolProxyNumber || undefined,
      cifDocument: args.cifDocument || undefined,
      cifDocumentImage: args.cifDocumentImage || undefined,
      deedCompany: args.deedCompany || undefined,
      deedCompanyImage: args.deedCompanyImage || undefined,
      companyStatutes: args.companyStatutes || undefined,
      companyStatutesImage: args.companyStatutesImage || undefined,
      realOwnership: args.realOwnership || undefined,
      realOwnershipImage: args.realOwnershipImage || undefined,
      idRepresentatives: args.idRepresentatives || undefined,
      idRepresentativesImage: args.idRepresentativesImage || undefined,
      notarialDeed: args.notarialDeed || undefined,
      notarialDeedImage: args.notarialDeedImage || undefined,
      userId: args.userId,
      personType: args.personType,
      investorType: new InvestorType({...args.investorType,
        type: EnumTypes.getInvestorTypeName(args.investorType.type)}),
      isPublicProfile: args.isPublicProfile || false,
      percentCompleted: args.percentCompleted || 0,
    });
  }

  public toRequest(juridicalPerson: JuridicalPerson): ApiJuridicalPersonCreateRequest {
    const maritalStatus = EnumTypes.returnMaritalStatusName(juridicalPerson.maritalStatus);
    const maritalEconomicStatus =
      EnumTypes.returnMaritalEconomicStatusName(juridicalPerson.maritalEconomicStatus);
    const maritalPropertyRegime =
      EnumTypes.returnMaritalPropertyRegimeName(juridicalPerson.maritalPropertyRegime);
    const occupationType = EnumTypes.returnOccupationTypesName(juridicalPerson.occupationType);
    return ({
      ...juridicalPerson,
      maritalStatus: maritalStatus,
      maritalEconomicStatus: maritalEconomicStatus,
      maritalPropertyRegime: maritalPropertyRegime,
      occupationType: occupationType,
    });
  }

  public fromFormData(data: JuridicalPerson): JuridicalPerson {
    return new JuridicalPerson(data as JuridicalPerson);
  }
}



// WEBPACK FOOTER //
// ./src/services/entity-factory/factories/JuridicalPersonFactory.tsx